/**
 * Every photo of a mould.
 *
 * A mould has as many pictures as somebody took - the Patalganga sheet has up
 * to ten on one tool - where it used to have exactly one field. The first is
 * the main photo, the one every list and the label show; any can be made the
 * main one.
 *
 * Viewing is built for many: a grid to see them all, and a viewer that steps
 * through them - arrows, ← → keys, a swipe on a phone - with "3 of 10" and a
 * strip of thumbnails to jump straight to one.
 *
 * Online only. The gallery lives on the server; a device keeps just the main
 * photo for its offline lists, so an offline visit says so instead of showing
 * an empty gallery that looks like data loss.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ImageOff, ImagePlus, Link2, Loader2, RefreshCw, Star, Trash2, WifiOff } from 'lucide-react';
import type { MouldPhoto } from '@mouldtrack/shared';
import { Button } from '../../components/ui/button.js';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '../../components/ui/primitives.js';
import { ConfirmDialog, Dialog } from '../../components/ui/dialog.js';
import {
  ApiClientError,
  isNetworkError,
  listMouldPhotos,
  makePrimaryPhoto,
  removeMouldPhoto,
  uploadMouldPhoto,
} from '../../api/client.js';
import { fileToJpegBlob } from '../../lib/image.js';
import { formatBytes, formatDate } from '../../lib/format.js';
import { cn } from '../../lib/cn.js';
import { requestSync } from '../../sync/engine.js';

const SOURCE_LABEL: Record<string, string> = {
  import: 'From a spreadsheet',
  upload: 'Uploaded',
  form: 'From the edit form',
  migration: 'Original photo',
};

/** Larger than any phone photo; anything bigger is a mistake, not a picture. */
const MAX_FILE_BYTES = 40 * 1024 * 1024;
const IMAGE_TYPES = /^image\/(jpeg|png|webp|gif|bmp|heic|heif)$/i;

function explain(e: unknown, fallback: string): string {
  return e instanceof ApiClientError ? e.message : fallback;
}

/** A picture that may fail to load: show a tile saying so, never a broken glyph. */
function Picture({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (failed) {
    return (
      <span className={cn('flex flex-col items-center justify-center gap-1 bg-surface-sunken text-xs text-subtle-foreground', className)}>
        <ImageOff className="size-5" aria-hidden />
        Photo unavailable
      </span>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={className} />;
}

export function MouldPhotoGallery({
  mouldId,
  name,
  canEdit,
  openSignal = 0,
}: {
  mouldId: string;
  name: string;
  canEdit: boolean;
  /** Bumped by the page to open the viewer on the main photo. */
  openSignal?: number;
}) {
  const [photos, setPhotos] = useState<MouldPhoto[] | null>(null);
  const [offline, setOffline] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [removing, setRemoving] = useState<MouldPhoto | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const touchX = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setPhotos(await listMouldPhotos(mouldId));
      setOffline(false);
    } catch (e) {
      if (isNetworkError(e)) setOffline(true);
      else setLoadError(explain(e, 'The photos could not be loaded.'));
    }
  }, [mouldId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The page asked to show the photos: open the viewer on the main one.
  useEffect(() => {
    if (!openSignal) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (photos?.length) setViewing(0);
  }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = photos?.length ?? 0;
  const current = viewing !== null && photos ? photos[viewing] : null;
  const step = useCallback(
    (by: number) => setViewing((v) => (v === null || count === 0 ? v : (v + by + count) % count)),
    [count],
  );

  // ← → while the viewer is open.
  useEffect(() => {
    if (viewing === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing, step]);

  /** The list changed on the server; the mould's main photo may have too. */
  const changed = (next: MouldPhoto[]) => {
    setPhotos(next);
    requestSync();
  };

  async function add(files: FileList) {
    setError(null);
    const failures: string[] = [];
    let added = 0;
    for (const [i, file] of [...files].entries()) {
      if (!IMAGE_TYPES.test(file.type)) {
        failures.push(`${file.name}: not a photo (JPEG, PNG, WebP, GIF or BMP)`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        failures.push(`${file.name}: ${formatBytes(file.size)} is too large`);
        continue;
      }
      setBusy(`Adding ${i + 1} of ${files.length}…`);
      try {
        const out = await uploadMouldPhoto(mouldId, await fileToJpegBlob(file));
        if (out.duplicate) failures.push(`${file.name}: this mould already has that photo`);
        else added++;
      } catch (e) {
        if (isNetworkError(e)) {
          failures.push(`${file.name} and any after it: no connection - nothing more was sent`);
          setOffline(true);
          break;
        }
        failures.push(`${file.name}: ${explain(e, 'could not be added')}`);
      }
    }
    setBusy(null);
    if (failures.length) setError(`${added ? `${added} added. ` : ''}${failures.join(' · ')}`);
    await load();
    requestSync();
  }

  async function primary(photo: MouldPhoto) {
    setBusy('Saving…');
    setError(null);
    try {
      changed(await makePrimaryPhoto(mouldId, photo.id));
      setViewing(0);
    } catch (e) {
      setError(isNetworkError(e) ? 'No connection - the main photo was not changed.' : explain(e, 'Could not change the main photo.'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(photo: MouldPhoto) {
    setBusy('Removing…');
    setError(null);
    try {
      const next = await removeMouldPhoto(mouldId, photo.id);
      changed(next);
      setViewing(next.length ? Math.min(viewing ?? 0, next.length - 1) : null);
    } catch (e) {
      setError(isNetworkError(e) ? 'No connection - the photo was not removed.' : explain(e, 'Could not remove the photo.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div ref={cardRef} className="scroll-mt-4">
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>
          Photos{photos ? <span className="ml-1.5 font-normal text-muted-foreground">{count}</span> : null}
        </CardTitle>
        {canEdit && !offline && photos ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void add(e.target.files);
                e.target.value = '';
              }}
            />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={!!busy}>
              <ImagePlus className="size-4" /> Add photos
            </Button>
          </>
        ) : null}
      </CardHeader>
      <CardBody>
        {offline ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <WifiOff className="size-4" aria-hidden /> The photo gallery loads when this device is online. The main
            photo above is kept on the device.
            <Button variant="ghost" size="sm" onClick={() => void load()}>
              <RefreshCw className="size-3.5" /> Try again
            </Button>
          </p>
        ) : loadError ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-danger">
            {loadError}
            <Button variant="ghost" size="sm" onClick={() => void load()}>
              <RefreshCw className="size-3.5" /> Try again
            </Button>
          </p>
        ) : photos === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Loading photos…
          </p>
        ) : count === 0 ? (
          <p className="text-sm text-muted-foreground">No photos yet.</p>
        ) : (
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-7">
            {photos.map((p, i) => (
              <li key={p.id} className="relative">
                <button
                  type="button"
                  onClick={() => setViewing(i)}
                  className="block aspect-square w-full overflow-hidden rounded-lg border border-border bg-surface-sunken focus-visible:outline-2 focus-visible:outline-primary"
                  aria-label={`Photo ${i + 1} of ${count}${i === 0 ? ', main photo' : ''}`}
                >
                  <Picture src={p.url} alt="" className="size-full object-cover" />
                </button>
                {i === 0 ? (
                  <Badge tone="primary" className="pointer-events-none absolute left-1 top-1">
                    <Star className="size-3" /> Main
                  </Badge>
                ) : null}
                {p.sharedWith?.length ? (
                  <Badge
                    tone="neutral"
                    className="pointer-events-none absolute bottom-1 left-1"
                    title={`The same picture is also on ${p.sharedWith.join(', ')}`}
                  >
                    <Link2 className="size-3" /> shared
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {busy ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden /> {busy}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="mt-2 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </CardBody>

      <Dialog
        open={!!current}
        onOpenChange={(open) => !open && setViewing(null)}
        title={name}
        description={
          current
            ? `Photo ${(viewing ?? 0) + 1} of ${count}${viewing === 0 ? ' · main photo' : ''} · ${SOURCE_LABEL[current.source] ?? 'Photo'} · ${formatDate(current.createdAt)} · ${formatBytes(current.bytes)}`
            : undefined
        }
        size="lg"
        footer={
          current && canEdit ? (
            <>
              <Button variant="ghost" onClick={() => setRemoving(current)} disabled={!!busy}>
                <Trash2 className="size-4" /> Remove
              </Button>
              {viewing !== 0 ? (
                <Button variant="primary" onClick={() => void primary(current)} loading={busy === 'Saving…'}>
                  <Star className="size-4" /> Make main photo
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      >
        {current ? (
          <div className="flex flex-col gap-3 pb-2">
            <div
              className="relative flex items-center justify-center rounded-lg bg-surface-sunken"
              onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
              onTouchEnd={(e) => {
                const start = touchX.current;
                const end = e.changedTouches[0]?.clientX;
                touchX.current = null;
                if (start === null || end === undefined || Math.abs(end - start) < 40) return;
                step(end < start ? 1 : -1);
              }}
            >
              <Picture key={current.id} src={current.url} alt={`${name}, photo ${(viewing ?? 0) + 1}`} className="max-h-[56dvh] min-h-48 w-full rounded-lg object-contain" />
              {count > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label="Previous photo"
                    className="absolute left-2 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-surface/90 text-foreground shadow-md hover:bg-surface"
                  >
                    <ChevronLeft className="size-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label="Next photo"
                    className="absolute right-2 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-surface/90 text-foreground shadow-md hover:bg-surface"
                  >
                    <ChevronRight className="size-5" />
                  </button>
                  <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-medium text-white tabular">
                    {(viewing ?? 0) + 1} / {count}
                  </span>
                </>
              ) : null}
            </div>
            {count > 1 ? (
              <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="All photos">
                {photos!.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={i === viewing}
                    aria-label={`Photo ${i + 1}`}
                    onClick={() => setViewing(i)}
                    className={cn(
                      'size-14 shrink-0 overflow-hidden rounded-md border-2',
                      i === viewing ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100',
                    )}
                  >
                    <Picture src={p.url} alt="" className="size-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
            {current.sharedWith?.length ? (
              <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
                <Link2 className="size-4 shrink-0" aria-hidden />
                The same picture is also on
                {current.sharedWith.map((code, i) => (
                  <span key={code}>
                    <Link
                      to={`/moulds/${encodeURIComponent(code)}`}
                      onClick={() => setViewing(null)}
                      className="font-medium text-foreground underline"
                    >
                      {code}
                    </Link>
                    {i < current.sharedWith!.length - 1 ? ',' : ''}
                  </span>
                ))}
                - the sheet gives these moulds the same photo. It is stored once and shown on each.
              </p>
            ) : null}
            {error ? <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">{error}</p> : null}
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Remove this photo?"
        description={`It is taken off this mould${removing && photos?.[0]?.id === removing.id && count > 1 ? ', and the next photo becomes the main one' : ''}. Backups taken before now still include it.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => {
          const target = removing;
          setRemoving(null);
          if (target) void remove(target);
        }}
      />
    </Card>
    </div>
  );
}
